import { motion } from 'framer-motion';
import { CheckCircle, Circle, Image, Music, Archive, Users } from 'lucide-react';
import { OnboardingProgress } from '../../hooks/useOnboardingProgress';

interface OnboardingProgressHeroProps {
  progress: OnboardingProgress;
}

const STEPS = [
  {
    key: 'hasProfileImage',
    label: 'Upload Profile Image',
    description: 'Add your artist profile photo to get started',
    icon: Image,
  },
  {
    key: 'hasVerifiedTrack',
    label: 'Verify a Track',
    description: 'Upload and get a track verified',
    icon: Music,
  },
  {
    key: 'hasPublishedVault',
    label: 'Publish a Pressing',
    description: 'Create and publish your first free Pressing',
    icon: Archive,
  },
  {
    key: 'hasAtLeastOneFan',
    label: 'Gain Your First Fan',
    description: 'Get your first fan to follow you',
    icon: Users,
  },
] as const;

/**
 * Onboarding Progress Hero — shows a 4-step progress bar on the artist dashboard.
 * Removed automatically when all steps are complete.
 */
export default function OnboardingProgressHero({ progress }: OnboardingProgressHeroProps) {
  if (progress.isComplete || progress.loading) return null;

  const progressPercent = (progress.completedSteps / progress.totalSteps) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8"
    >
      <div className="glass-card border border-electric-cyan/20 p-6 sm:p-8 rounded-xl relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-electric-cyan/5 rounded-full blur-[80px]" />
        
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-electric-cyan/70 block mb-1">
                setup::progress
              </span>
              <h3 className="text-lg font-semibold text-white">
                Complete Your Setup
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                {progress.completedSteps} of {progress.totalSteps} steps complete
              </p>
            </div>
            <div className="text-right">
              <span className="font-mono text-2xl font-bold text-electric-cyan">
                {Math.round(progressPercent)}%
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="h-2 bg-white/5 rounded-full overflow-hidden relative">
              {/* Step markers */}
              <div className="absolute inset-0 flex">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-full border-r border-deep-charcoal/50"
                    style={{ width: '25%' }}
                  />
                ))}
              </div>
              {/* Progress fill */}
              <motion.div
                className="h-full bg-gradient-to-r from-electric-cyan to-green-400 rounded-full relative"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
              >
                {/* Glow effect on the bar */}
                <div className="absolute inset-0 bg-electric-cyan/30 blur-sm rounded-full" />
              </motion.div>
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((step, idx) => {
              const isCompleted = progress[step.key as keyof OnboardingProgress] as boolean;
              const StepIcon = step.icon;
              return (
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * idx }}
                  className={`rounded-lg border p-4 transition-all duration-300 ${
                    isCompleted
                      ? 'border-green-500/30 bg-green-500/5'
                      : 'border-white/10 bg-white/[0.02]'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500/20'
                        : 'bg-white/5'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <StepIcon className="w-4 h-4 text-text-muted" />
                      )}
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
                      Step {idx + 1}
                    </span>
                  </div>
                  <h4 className={`text-sm font-medium mb-1 ${
                    isCompleted ? 'text-green-400' : 'text-white'
                  }`}>
                    {step.label}
                  </h4>
                  <p className="text-xs text-text-muted leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
